import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  lazy,
  Suspense
} from "react";
import { useSubmitAssessment, saveUuid } from "../../utils/assessment";
import { getChartData } from "../../utils/weightLossTrajectory";
import { getBMI } from "../../utils/bmi";
import useAppContext from "../AppContext";
import useUrlSlugContext from "../UrlSlugContext";
import { cloneDeep } from "lodash";
import { Redirect } from "react-router-dom";
import routes from "../../routes";
import Error from "./Error";
import LoadingInterstitial from "./LoadingInterstitial";
import SequenceLoadingInterstitial from "./SequenceLoadingInterstitial";
import RedirectFirstQuestion from "../RedirectFirstQuestion";
import { pageView, dispatchEvent, setPageData } from "../../utils/analytics";
import { SECONDS_IN_SIX_MONTHS, setCookieValue } from "../../utils/cookies";
import {
  hasAnsweredAllQuestions,
  getNextQuestion
} from "../../utils/branching";
import { RESULTS_ELI_SEQUENCE } from "../../utils/eligibility";
import {
  POINTS_WORKSHOP_PROGRAM,
  POINTS_PROGRAM,
  DIABETES_WORKSHOP_PROGRAM,
  DIABETES_PROGRAM,
  GLP1_PROGRAM,
  CLINICAL_PROGRAM,
  resultToEventMap
} from "../../utils/resultFunctions/constants";
import useCreateClinicSub from "../../utils/useCreateClinicSub";
import {
  saveEliResultsData,
  saveUniResultsData,
  saveDefaultResultsData,
  saveFoodNoiseResultsData
} from "./storeResultsData";
import { sendDataToGpc } from "../../utils/gpc";
import getSessionData from "../../utils/getSessionData";
import { logger } from "utils/logger";

const Results = lazy(
  () => import(/* webpackChunkName: "results" */ "./Results")
);

const getUserPredictedToReachGoal = profile => {
  if (!profile.imperial || !profile.metric) {
    return undefined;
  }

  const desiredWeightLoss =
    profile.imperial.weight - profile.imperial.desiredWeight;

  const { estimatedWeightChange } = getChartData(
    profile.gender,
    getBMI(
      profile.metric.height,
      profile.metric.weight,
      profile.metric.heightUnit
    ),
    desiredWeightLoss
  );

  return estimatedWeightChange < desiredWeightLoss ? false : true;
};

const getUserRecommendedPlanLength = profile => {
  if (!profile.imperial || !profile.metric) {
    return undefined;
  }

  const desiredWeightLoss =
    profile.imperial.weight - profile.imperial.desiredWeight;

  const { results } = getChartData(
    profile.gender,
    getBMI(
      profile.metric.height,
      profile.metric.weight,
      profile.metric.heightUnit
    ),
    desiredWeightLoss
  );
  // 12 or more weeks = 10 month plan.
  return results.length >= 12 ? 10 : 3;
};

let quizEndedEventTriggered = false;
let productRecEventTriggered = false;
export default function ResultsSkeleton() {
  const savedUuid = useRef(false);
  const { assessment, answers, profile, contact, newQuiz, nextStack } =
    useAppContext();
  const { market } = contact;
  const { urlSlug, baseUrl, queryString } = useUrlSlugContext();
  const [userPredictedToReachGoal, setUserPredictedToReachGoal] = useState(
    getUserPredictedToReachGoal(profile)
  );
  const [userRecommendedPlanLength, setUserRecommendedPlanLength] = useState(
    getUserRecommendedPlanLength(profile)
  );

  const { createIneligibleUser } = useCreateClinicSub();

  const {
    suggestedProduct,
    assessmentId,
    submit,
    isSubmitting,
    isFailure,
    isPristine,
    isSuccess,
    eligibleSequence,
    resultsScreen
  } = useSubmitAssessment();

  const isAssessmentComplete = hasAnsweredAllQuestions(answers, nextStack);

  useEffect(() => {
    if (newQuiz.isFoodNoise) {
      return;
    }

    pageView("result");

    if (!quizEndedEventTriggered) {
      quizEndedEventTriggered = true;

      dispatchEvent({
        event: "non_click_event",
        event_key: newQuiz.isUni ? "unified_quiz_ended" : "quiz:ended",
        event_category: "quiz",
        event_action: newQuiz.isUni ? "unified_quiz_ended" : "quiz_ended"
      });
    }
  }, [JSON.stringify(newQuiz)]);

  useEffect(() => {
    if (!isAssessmentComplete) {
      return;
    }
    if (!newQuiz.isUni) {
      return;
    }
    if (!resultsScreen) {
      return;
    }
    if (!isSuccess) {
      return;
    }
    if (!assessmentId) {
      return;
    }
    if (
      ![
        POINTS_WORKSHOP_PROGRAM,
        POINTS_PROGRAM,
        DIABETES_WORKSHOP_PROGRAM,
        DIABETES_PROGRAM,
        GLP1_PROGRAM,
        CLINICAL_PROGRAM
      ].includes(resultsScreen)
    ) {
      return;
    }

    async function sendEmail() {
      try {
        const response = await sendDataToGpc();

        if (!response) {
          return;
        }

        const { status, data } = response;

        if (status !== 200) {
          return;
        }

        if (data?.uuid) {
          setPageData({
            user_uuid: data.uuid
          });

          dispatchEvent({
            event: "non_click_event",
            event_key: "quiz_user_identified",
            event_category: "quiz",
            event_action: "user_identified"
          });
          await saveUuid({ assessmentId, uuid: data.uuid });
        }
      } catch (error) {
        logger("Error sending data to GPC", {
          level: "error",
          customAttributes: { error }
        });
        return null;
      }
    }

    if (savedUuid.current) {
      return;
    }
    savedUuid.current = true;

    sendEmail();
  }, [
    isAssessmentComplete,
    JSON.stringify(newQuiz),
    resultsScreen,
    isSuccess,
    assessmentId
  ]);

  const handleSubmit = useCallback(() => {
    submit({
      answers,
      newQuiz,
      questions: assessment.questions,
      userPredictedToReachGoal,
      userRecommendedPlanLength,
      createIneligibleUser,
      profile,
      market
    });
  }, [
    submit,
    answers,
    // useXS triggered 2 times which caused
    // newQuiz object to be created 2 times
    // which caused calling submit() 2 times
    // though the values inside are the same.
    // that's why JSON.stringify is needed.
    JSON.stringify(newQuiz),
    assessment,
    userPredictedToReachGoal,
    userRecommendedPlanLength
  ]);

  useEffect(() => {
    if (isAssessmentComplete) {
      handleSubmit();
    }
  }, [
    submit,
    answers,
    assessment,
    JSON.stringify(newQuiz),
    isAssessmentComplete,
    contact,
    handleSubmit
  ]);

  useEffect(() => {
    setUserPredictedToReachGoal(getUserPredictedToReachGoal(profile));
    setUserRecommendedPlanLength(getUserRecommendedPlanLength(profile));
  }, [profile]);

  useEffect(() => {
    if (
      !newQuiz.eli &&
      !newQuiz.isUni &&
      !newQuiz.isFoodNoise &&
      suggestedProduct &&
      !productRecEventTriggered
    ) {
      productRecEventTriggered = true;

      dispatchEvent({
        event: "non_click_event",
        event_key: `quiz:recommended_product:${suggestedProduct}`,
        event_category: "quiz",
        event_action: "recommended_product",
        event_label: suggestedProduct
      });
    }
  }, [suggestedProduct, JSON.stringify(newQuiz)]);

  useEffect(() => {
    if (!resultsScreen) {
      return;
    }

    if (newQuiz.eli || newQuiz.isSequence || newQuiz.isUni) {
      if (newQuiz.isUni) {
        setPageData({
          quiz_result: resultsScreen
        });
      }

      dispatchEvent({
        event: "non_click_event",
        event_key: `quiz:result:${resultsScreen}`,
        event_category: "quiz",
        event_action: "quizResult",
        event_label: resultsScreen
      });
    }

    // Used for email campaigns. Only for logged in quizzes.
    const { uuid } = getSessionData();
    if ((newQuiz.isUni3 || newQuiz.isUni4 || newQuiz.isSequence) && uuid) {
      dispatchEvent({
        event: "non_click_event",
        event_key: "assessment_end",
        event_category: "assessment",
        event_action: "assesment_end",
        user_id: uuid,
        assessment_type: urlSlug,
        assessment_result: resultToEventMap[resultsScreen]
      });
      setPageData({
        assessment_result: resultToEventMap[resultsScreen]
      });
    }
  }, [resultsScreen, JSON.stringify(newQuiz)]);

  useEffect(() => {
    if (!assessmentId) {
      return;
    }

    if (newQuiz.isUni) {
      saveUniResultsData({
        assessmentId,
        answers,
        assessment,
        baseUrl,
        resultsScreen,
        profile
      });
    } else if (newQuiz.eli) {
      saveEliResultsData({
        assessment,
        assessmentId,
        profile,
        resultsScreen,
        eligibleSequence,
        newQuiz,
        baseUrl,
        answers
      });
    } else if (newQuiz.isSequence) {
      const cookie = {
        aid: assessmentId
      };

      cookie.resultsScreenSequence = resultsScreen;

      if (resultsScreen === RESULTS_ELI_SEQUENCE) {
        cookie.profile = cloneDeep(profile || {});
        cookie.queryString = queryString;
      }
      setCookieValue("ww_assessment", cookie, SECONDS_IN_SIX_MONTHS);
    } else if (newQuiz.isFoodNoise) {
      saveFoodNoiseResultsData({
        assessmentId,
        profile,
        baseUrl,
        resultsScreen
      });
    } else {
      // default assessment.
      saveDefaultResultsData({
        baseUrl,
        assessmentId,
        suggestedProduct,
        assessment,
        profile,
        answers
      });
    }
  }, [
    profile,
    resultsScreen,
    suggestedProduct,
    assessmentId,
    eligibleSequence,
    JSON.stringify(newQuiz),
    baseUrl
  ]);

  if (!isAssessmentComplete) {
    const nextQuestion = getNextQuestion(
      nextStack,
      null,
      assessment,
      answers,
      false,
      true
    );

    if (nextQuestion) {
      return <Redirect to={routes.question(nextQuestion.urlPath)} />;
    } else {
      return <RedirectFirstQuestion />;
    }
  }

  if (isFailure) {
    return <Error retry={handleSubmit} isSubmitting={isSubmitting} />;
  }

  if (isPristine || isSubmitting) {
    return null;
  }

  const props = {
    suggestedProduct,
    profile,
    newQuiz,
    questions: assessment.questions,
    answers,
    eligibleSequence,
    eliUrl: newQuiz.eliUrl,
    coreUrl: newQuiz.coreUrl,
    aid: assessmentId,
    resultsScreen
  };

  return (
    // A fallback is needed for the Suspense component, or a blank screen is shown for a very long time on some browsers
    <Suspense fallback={!newQuiz.isSequence && <LoadingInterstitial />}>
      <>
        {!newQuiz.isSequence ? (
          <>
            <LoadingInterstitial />
            <Results {...props} />
          </>
        ) : (
          <SequenceLoadingInterstitial>
            <Results {...props} />
          </SequenceLoadingInterstitial>
        )}
      </>
    </Suspense>
  );
}
